/**
 * The one definition of "this policy has enough data to verify".
 *
 * Why it exists: breakdown/estimate scrapes were failing at the carrier with
 * "We couldn't find the claimant" because nobody checked the policy first. The
 * backend only ever rejected a missing member_id (and contract_number for
 * SunLife), and nothing anywhere checked the member's date of birth — it just
 * degraded to an empty string when the scrape arguments were built.
 *
 * Kept in one module so the request flow, the Instant-AI flow and anything added
 * later can't drift apart on what "complete" means.
 *
 * The member's NAME is part of the set because every carrier form takes it, and
 * a policy without one can't be looked up by a human either.
 *
 * This is a line-for-line port of clinics/services/policy_completeness.py, which
 * is now the ENFORCING copy: the insurance-request endpoint rejects a request
 * for a policy missing any of these. Keep the two in step.
 */

export const CRITICAL_FIELDS = [
  "first_name",
  "last_name",
  "member_id",
  "contract_number",
  "date_of_birth",
];

/**
 * What a review surface SHOWS, which is deliberately more than what it REQUIRES.
 *
 * `relation_to_member` is here and not in CRITICAL_FIELDS, and the distinction is
 * the whole point. It is not one more identifier to collect — it is the field
 * that says who the other five describe. Reviewing a member name, ID, contract
 * and DOB without it is reviewing four values whose meaning depends on a fifth
 * you cannot see: for a spouse or child policy those fields are the SUBSCRIBER's,
 * and 39% of active policies are exactly that.
 *
 * A real case: a policy stored `self` with the patient's own name and DOB, so the
 * bot told SunLife the 23-year-old daughter was the plan member. The carrier
 * answered "We couldn't find the claimant" and the five visible fields all looked
 * perfectly filled in.
 *
 * NOT added to CRITICAL_FIELDS on purpose. That list gates sending and lights the
 * FTC Data Entry chips; 431 active policies (8.5%) have no relation stored, and
 * they would all turn red the day this shipped. Showing it fixes the blind spot;
 * requiring it would just move the blockage.
 */
export const REVIEW_FIELDS = CRITICAL_FIELDS.concat(["relation_to_member"]);

/** Options for the relation select. Mirrors the Manage Policies modal and
 *  clinics.views' `valid_relations`, which the update endpoint validates against. */
export const RELATION_OPTIONS = [
  { value: "self", label: "Self" },
  { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" },
  { value: "other", label: "Other" },
];

export const FIELD_LABELS = {
  first_name: "Member first name",
  last_name: "Member last name",
  member_id: "Member / Subscriber ID",
  contract_number: "Group / Contract #",
  date_of_birth: "Member date of birth",
  relation_to_member: "Patient relation to member",
};

// Short forms for inline prose ("missing member ID and date of birth").
export const FIELD_LABELS_SHORT = {
  first_name: "member first name",
  last_name: "member last name",
  member_id: "member ID",
  contract_number: "group/contract #",
  date_of_birth: "member date of birth",
};

// Chip-length forms, for the FTC Data Entry chip — it already carries the work
// type and the carrier, so the field list has to stay tight.
export const FIELD_LABELS_TINY = {
  first_name: "first name",
  last_name: "last name",
  member_id: "member ID",
  contract_number: "group #",
  date_of_birth: "member DOB",
};

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

/** The critical fields this policy is missing, in CRITICAL_FIELDS order. */
export function missingCriticalFields(policy) {
  if (!policy) return CRITICAL_FIELDS.slice();
  return CRITICAL_FIELDS.filter(function (field) {
    return isBlank(policy[field]);
  });
}

export function isPolicyComplete(policy) {
  return missingCriticalFields(policy).length === 0;
}

/** True when any policy in the list is missing something. */
export function anyPolicyIncomplete(policies) {
  return (policies || []).some(function (p) {
    return !isPolicyComplete(p);
  });
}

/** "member ID and member date of birth" — for toasts and helper text. */
export function describeMissing(fields) {
  const names = (fields || []).map(function (f) {
    return FIELD_LABELS_SHORT[f] || f;
  });
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

/**
 * "Steven Havens (Self)" — WHO the plan member is, for a policy card.
 *
 * A patient can legitimately hold two policies with the same carrier: their own
 * plan and a spouse's, each covering the other (Sikka reports the carrier in
 * both blocks). A card that says only "Manulife · Primary" / "Manulife ·
 * Secondary" reads as the same policy listed twice; naming the member and their
 * relationship to the patient is what tells them apart. Empty string when the
 * policy names no member.
 */
export function describeMember(policy) {
  if (!policy) return "";
  const name = [policy.first_name, policy.last_name]
    .map(function (s) { return String(s || "").trim(); })
    .filter(Boolean)
    .join(" ");
  if (!name) return "";
  const rel = String(policy.relation_to_member || "").trim().toLowerCase();
  const opt = RELATION_OPTIONS.find(function (o) { return o.value === rel; });
  return opt ? name + " (" + opt.label + ")" : name;
}
